/**
 * Incident acknowledge & popup throttle (Monitor, Influx).
 * Hardware + agent alerts clear on recovery — no manual acknowledge.
 */

const INCIDENT_STATE_KEY = 'pingpulse_incident_state';
const DEFAULT_THRESHOLD = 5;
const DEFAULT_INTERVAL_SEC = 60;
const HARDWARE_INTERVAL_SEC = 300;

/** Popup throttle + optional ack (monitor / influx only). */
export const PHASE1_ALERT_TYPES = new Set(['target-status', 'influxdb']);

/** Recovery clears Active Alerts — no Ack button. */
export const AGENT_ALERT_TYPES_NO_ACK = new Set([
  'monitored-item',
  'resource',
  'event-anomaly',
  'hardware',
]);

function loadState() {
  try {
    const raw = localStorage.getItem(INCIDENT_STATE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function persistState(state) {
  localStorage.setItem(INCIDENT_STATE_KEY, JSON.stringify(state));
}

/** Popup throttle types (monitor, influx, hardware). */
export const POPUP_THROTTLE_ALERT_TYPES = new Set(['target-status', 'influxdb', 'hardware']);

export function phase1IncidentKey(type, id) {
  if (id == null) return '';
  if (type === 'hardware') return `hardware:${String(id)}`;
  if (type === 'influxdb') return `influxdb:${String(id)}`;
  if (type === 'target-status') return `target-status:${String(id)}`;
  return '';
}

export function phase1KeyFromAlert(alert) {
  if (!alert || !POPUP_THROTTLE_ALERT_TYPES.has(alert.type)) return '';
  const d = alert.data || {};
  if (alert.type === 'hardware') {
    return phase1IncidentKey('hardware', d.agent_id ?? d.agentId);
  }
  const targetId = alert.targetId ?? d.targetId ?? d.target_id ?? d.influxTargetId;
  if (alert.type === 'influxdb') return phase1IncidentKey('influxdb', targetId);
  return phase1IncidentKey('target-status', targetId);
}

/** Payload for POST /alerts/incidents/acknowledge (monitor + influx only). */
export function phase1AckRequestFromAlert(alert) {
  if (!alert || !PHASE1_ALERT_TYPES.has(alert.type)) return null;
  const d = alert.data || {};
  const targetId = alert.targetId ?? d.targetId ?? d.target_id ?? d.influxTargetId;
  return targetId != null ? { type: alert.type, targetId } : null;
}

export function computeAckRuleMs(consecutiveThreshold, checkIntervalSec) {
  const threshold = Math.max(1, Number(consecutiveThreshold) || DEFAULT_THRESHOLD);
  const intervalSec = Math.max(1, Number(checkIntervalSec) || DEFAULT_INTERVAL_SEC);
  return 2 * threshold * intervalSec * 1000;
}

export function registerPhase1Incident(type, id, options = {}) {
  const key = phase1IncidentKey(type, id);
  if (!key) return null;

  const state = loadState();
  const existing = state[key];
  const threshold = options.consecutiveThreshold ?? existing?.consecutiveThreshold ?? DEFAULT_THRESHOLD;
  const intervalSec = options.checkIntervalSec ?? existing?.checkIntervalSec ?? (
    type === 'hardware' ? HARDWARE_INTERVAL_SEC : DEFAULT_INTERVAL_SEC
  );

  if (!existing || existing.status === 'recovered') {
    state[key] = {
      type,
      id: String(id),
      startedAt: options.startedAt ?? Date.now(),
      lastPopupAt: null,
      acknowledged: false,
      acknowledgedAt: null,
      consecutiveThreshold: threshold,
      checkIntervalSec: intervalSec,
      ruleMs: computeAckRuleMs(threshold, intervalSec),
      status: 'active',
    };
    persistState(state);
  }
  return state[key];
}

export function isPhase1IncidentAcknowledged(type, id) {
  const key = phase1IncidentKey(type, id);
  if (!key) return false;
  const incident = loadState()[key];
  return Boolean(incident?.acknowledged);
}

function notifyPhase1AckChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('pingpulse-phase1-ack'));
}

export function markPhase1IncidentAcknowledged(alert) {
  const key = phase1KeyFromAlert(alert);
  if (!key) return;
  const state = loadState();
  if (!state[key]) {
    registerPhase1Incident(alert.type, alert.targetId ?? alert.data?.targetId, {});
  }
  const next = loadState();
  if (next[key]) {
    next[key].acknowledged = true;
    next[key].acknowledgedAt = Date.now();
    persistState(next);
    notifyPhase1AckChange();
  }
}

export function markAllPhase1IncidentsAcknowledged(alerts) {
  let changed = false;
  (alerts || []).forEach((alert) => {
    if (!PHASE1_ALERT_TYPES.has(alert.type)) return;
    const key = phase1KeyFromAlert(alert);
    if (!key) return;
    if (!loadState()[key]) {
      registerPhase1Incident(alert.type, alert.targetId ?? alert.data?.targetId, {});
    }
    const next = loadState();
    if (next[key] && !next[key].acknowledged) {
      next[key].acknowledged = true;
      next[key].acknowledgedAt = Date.now();
      persistState(next);
      changed = true;
    }
  });
  if (changed) notifyPhase1AckChange();
}

export function shouldShowAckButton(alert) {
  if (!alert) return false;
  if (AGENT_ALERT_TYPES_NO_ACK.has(alert.type)) return false;
  if (!PHASE1_ALERT_TYPES.has(alert.type)) return true;
  const key = phase1KeyFromAlert(alert);
  if (!key) return true;
  const incident = loadState()[key];
  if (!incident || incident.status === 'recovered') return true;
  return !incident.acknowledged;
}

export function countPendingAcknowledgements(alerts) {
  if (!Array.isArray(alerts)) return 0;
  return alerts.filter(shouldShowAckButton).length;
}

export function clearPhase1Incident(type, id) {
  const key = phase1IncidentKey(type, id);
  if (!key) return;
  const state = loadState();
  if (state[key]) {
    delete state[key];
    persistState(state);
    notifyPhase1AckChange();
  }
}

export function clearPhase1IncidentByKey(key) {
  if (!key) return;
  const state = loadState();
  if (state[key]) {
    delete state[key];
    persistState(state);
  }
}

/** Clear monitor + influx keys for a target id string. */
export function clearPhase1IncidentsForTargetId(targetId) {
  if (targetId == null) return;
  const id = String(targetId);
  clearPhase1Incident('target-status', id);
  clearPhase1Incident('influxdb', id);
}

export function clearPhase1OnTargetRecovery(targetId) {
  clearPhase1IncidentsForTargetId(targetId);
}

export function clearPhase1OnInfluxRecovery(influxTargetId) {
  clearPhase1IncidentsForTargetId(influxTargetId);
}

export function clearPhase1OnHardwareRecovery(agentId) {
  clearPhase1Incident('hardware', agentId);
}

/**
 * Popup throttle for phase 1: first popup immediate, then at most once per ruleMs window.
 */
export function canShowPhase1Popup(type, id) {
  if (!POPUP_THROTTLE_ALERT_TYPES.has(type)) return true;
  const key = phase1IncidentKey(type, id);
  const entry = loadState()[key];
  if (!entry) return true;
  const now = Date.now();
  if (entry.lastPopupAt == null) return true;
  return now - entry.lastPopupAt >= entry.ruleMs;
}

export function recordPhase1PopupShown(type, id, options = {}) {
  registerPhase1Incident(type, id, options);
  const state = loadState();
  const key = phase1IncidentKey(type, id);
  if (!state[key]) return;
  state[key].lastPopupAt = Date.now();
  persistState(state);
}

export function shouldShowPhase1Popup(type, id, options = {}) {
  if (!canShowPhase1Popup(type, id)) return false;
  recordPhase1PopupShown(type, id, options);
  return true;
}

export function isPhase1FailureStatus(type, status) {
  const s = String(status || '').toUpperCase();
  if (type === 'target-status') return s === 'DOWN' || s === 'ERROR';
  if (type === 'influxdb') return s === 'ANOMALY' || s === 'DOWN' || s === 'ERROR';
  return false;
}
